import { useEffect } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export function useCinematicScroll() {
  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero title: letters drop in ──────────────────────────────────────
      const titleEl = document.querySelector<HTMLElement>(".cin-title");
      if (titleEl) {
        const split = new SplitText(titleEl, { type: "chars" });
        gsap.from(split.chars, {
          opacity: 0,
          y: 60,
          rotateX: -90,
          stagger: 0.04,
          duration: 1,
          ease: "power3.out",
          delay: 0.3,
        });
      }

      // ── Hero subtitle fade ────────────────────────────────────────────────
      gsap.from(".cin-subtitle", {
        opacity: 0,
        y: 30,
        duration: 1,
        delay: 1.2,
        ease: "power2.out",
      });

      // ── Scroll indicator pulse ────────────────────────────────────────────
      gsap.to(".cin-scroll-indicator", {
        y: 10,
        opacity: 0,
        repeat: -1,
        yoyo: false,
        duration: 1.2,
        ease: "power1.inOut",
      });

      // ── Chapter 1: Date — pinned reveal ──────────────────────────────────
      gsap.from(".cin-date-number", {
        textContent: 0,
        duration: 1.5,
        snap: { textContent: 1 },
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".cin-chapter-date",
          start: "top 80%",
          toggleActions: "play reverse play reverse",
        },
      });

      gsap.from(".cin-date-label", {
        opacity: 0,
        x: -40,
        stagger: 0.15,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".cin-chapter-date",
          start: "top 70%",
          toggleActions: "play reverse play reverse",
        },
      });

      // Clip-path wipe on date chapter
      gsap.from(".cin-date-bg", {
        clipPath: "inset(0 100% 0 0)",
        duration: 1.4,
        ease: "power3.inOut",
        scrollTrigger: {
          trigger: ".cin-chapter-date",
          start: "top 60%",
          toggleActions: "play reverse play reverse",
        },
      });

      // ── Chapter 2: Scroll-synced frame sequence ────────────────────────
      // The hook in cinematic/index.tsx (useScrollFrameSequence) handles the
      // pin + scrub + canvas rendering. Here we only sync the quote overlays
      // off the same `cin-chapter-video` trigger using fixed scroll fractions.
      const quotes = document.querySelectorAll<HTMLElement>(".cin-video-quote");
      quotes.forEach((quote, i) => {
        const start = (i / quotes.length) * 0.8;
        const end = start + 0.2;
        ScrollTrigger.create({
          trigger: ".cin-chapter-video",
          start: `top+=${start * 300}% top`,
          end: `top+=${end * 300}% top`,
          toggleClass: { targets: quote, className: "opacity-100" },
        });
      });

      // ── Chapter 3: Story — line-by-line reveal ───────────────────────────
      const storyLines = document.querySelectorAll(".cin-story-line");
      storyLines.forEach((line, i) => {
        gsap.from(line, {
          opacity: 0,
          y: 30,
          duration: 0.8,
          delay: i * 0.08,
          ease: "power2.out",
          scrollTrigger: {
            trigger: line,
            start: "top 85%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      // ── Chapter 4: Venue — stagger cards ─────────────────────────────────
      gsap.from(".cin-venue-card", {
        opacity: 0,
        y: 50,
        scale: 0.95,
        stagger: 0.2,
        duration: 0.9,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".cin-chapter-venue",
          start: "top 75%",
          toggleActions: "play reverse play reverse",
        },
      });

      // ── Outro: form zoom in ───────────────────────────────────────────────
      gsap.from(".cin-form-wrapper", {
        opacity: 0,
        scale: 0.92,
        y: 60,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".cin-form-wrapper",
          start: "top 80%",
          toggleActions: "play reverse play reverse",
        },
      });
    });

    return () => ctx.revert();
  }, []);
}
